import { useState } from "react";
import { Loader2, Megaphone, Send } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useDisparos } from "@/hooks/useDisparos";
import { useDisparosRiskAck } from "@/hooks/useDisparosRiskAck";
import { DisparoNewWizard } from "@/components/disparos/DisparoNewWizard";
import { DisparoCampaignsTab } from "@/components/disparos/DisparoCampaignsTab";
import { DisparoCleanupDialog } from "@/components/disparos/DisparoCleanupDialog";
import { DisparoRiskAcknowledgment } from "@/components/disparos/DisparoRiskAcknowledgment";

export default function Disparos() {
  const { user } = useAuth();
  const { workspaceId } = useWorkspace(user?.id);
  const { acknowledged, checking, markAcknowledged } = useDisparosRiskAck(user?.id, workspaceId);
  const [tab, setTab] = useState("new");
  const [cleanupOpen, setCleanupOpen] = useState(false);

  const { campaigns, loading, refetch, controlCampaign, reschedule, cleanup } = useDisparos(workspaceId, {
    enabled: acknowledged && tab === "campaigns",
  });

  if (!workspaceId) return null;

  const pageHeader = (
    <div className="shrink-0">
      <h1 className="text-xl font-semibold tracking-tight">Disparos</h1>
      <p className="text-sm text-muted-foreground mt-0.5">
        Envie mensagens em massa para leads selecionados via WhatsApp
      </p>
    </div>
  );

  const pageShellClass = "flex flex-col h-full min-h-0 overflow-hidden px-3 sm:px-4 py-4 gap-3";

  if (checking) {
    return (
      <div className={pageShellClass}>
        {pageHeader}
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!acknowledged) {
    return (
      <div className={pageShellClass}>
        {pageHeader}
        <DisparoRiskAcknowledgment
          isOpen={true}
          workspaceId={workspaceId}
          onAccepted={markAcknowledged}
        />
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className={pageShellClass}>
      {pageHeader}

      <Tabs value={tab} onValueChange={setTab} className="flex flex-col flex-1 min-h-0 overflow-hidden">
        <TabsList className="h-8 w-fit shrink-0">
          <TabsTrigger value="new" className="gap-1.5 text-xs px-3">
            <Send className="h-3.5 w-3.5 shrink-0" />
            Novo disparo
          </TabsTrigger>
          <TabsTrigger value="campaigns" className="gap-1.5 text-xs px-3">
            <Megaphone className="h-3.5 w-3.5 shrink-0" />
            Campanhas
          </TabsTrigger>
        </TabsList>

        <TabsContent
          value="new"
          className="mt-3 flex flex-col flex-1 min-h-0 h-full overflow-hidden data-[state=inactive]:hidden"
        >
          <DisparoNewWizard
            workspaceId={workspaceId}
            onCreated={() => {
              refetch();
              setTab("campaigns");
            }}
          />
        </TabsContent>

        <TabsContent
          value="campaigns"
          className="mt-3 flex flex-col flex-1 min-h-0 h-full overflow-hidden data-[state=inactive]:hidden"
        >
          <div className="flex-1 min-h-0 overflow-y-auto">
            <DisparoCampaignsTab
              workspaceId={workspaceId}
              campaigns={campaigns}
              loading={loading}
              onPause={(id) => controlCampaign.mutate({ campaignId: id, action: "pause" })}
              onResume={(id) => controlCampaign.mutate({ campaignId: id, action: "resume" })}
              onDelete={(id) => controlCampaign.mutate({ campaignId: id, action: "delete" })}
              onReschedule={(id, values) =>
                reschedule.mutate({
                  campaignId: id,
                  integrationId: values.integration_id,
                  scheduledAt: values.scheduled_at,
                })
              }
              onCleanup={() => setCleanupOpen(true)}
              controlling={controlCampaign.isPending}
              rescheduling={reschedule.isPending}
            />
          </div>
        </TabsContent>
      </Tabs>

      <DisparoCleanupDialog
        open={cleanupOpen}
        onOpenChange={setCleanupOpen}
        loading={cleanup.isPending}
        onConfirm={(opts) => {
          cleanup.mutate(opts, { onSuccess: () => setCleanupOpen(false) });
        }}
      />
    </div>
  );
}
